import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  List,
  ListItem,
  CircularProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  Restaurant as RestaurantIcon,
  SmartToy as AIIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { all_tags } from '../helpers/all_tags';

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_MODEL = "gemini-1.5-flash-latest"; // Use the free tier, fast model

const ChatBotWithAI = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [useAI, setUseAI] = useState(false);
  
  const restaurants = useSelector((state) => state.restaurantsReducer.restaurants);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: 1,
          type: 'bot',
          content: `Hi! I'm your AI restaurant assistant. I can help you find the perfect restaurant based on your preferences. \n\nYou can ask me things like:\n• "I'm craving pizza"\n• "Show me halal restaurants near me"\n• "What's good for breakfast?"\n• "I want something spicy"\n• "Recommend a fine dining restaurant"\n\nWhat are you in the mood for today?`,
          timestamp: new Date()
        }
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const callGemini = async (prompt) => {
    if (!GEMINI_API_KEY) {
      throw new Error('Gemini API key not configured. Please add REACT_APP_GEMINI_API_KEY to your environment variables.');
    }
    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${GEMINI_API_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }]
          })
        }
      );
      if (!response.ok) {
        throw new Error('Failed to get Gemini response');
      }
      const data = await response.json();
      return (
        data.candidates?.[0]?.content?.parts?.[0]?.text ||
        'No response from Gemini.'
      );
    } catch (error) {
      console.error('Gemini API error:', error);
      throw new Error('AI service is currently unavailable. Please try again later.');
    }
  };

  const generateRecommendations = async (userMessage) => {
    setIsLoading(true);
    setError('');

    try {
      // Create a context about available restaurants
      const restaurantContext = restaurants.map(restaurant => ({
        name: restaurant.name,
        tags: restaurant.tags || [],
        description: restaurant.description || '',
        menu_status: restaurant.menu_status || '',
        rating: restaurant.rating || '',
        priceLevel: restaurant.priceLevel || '',
        address: restaurant.address || '',
        city: restaurant.city || ''
      })).slice(0, 30); // Limit to first 30 restaurants for context

      const availableTags = all_tags.join(', ');
      
      const prompt = `You are a helpful restaurant recommendation assistant for LontraFoods, a halal food discovery platform. \n\nAvailable restaurant categories/tags: ${availableTags}\n\nUser query: "${userMessage}"\n\nAvailable restaurants (limited sample):\n${JSON.stringify(restaurantContext, null, 2)}\n\nPlease provide 3-5 restaurant recommendations based on the user's query. For each recommendation, include:\n1. Restaurant name\n2. Why it matches their request\n3. Key features (tags, rating, price level if available)\n4. Brief description\n\nFormat your response as a friendly conversation, not a list. Keep it conversational and helpful. If no restaurants match the query, suggest alternative cuisines or ask for clarification.`;

      let recommendations;
      
      if (useAI) {
        // Use Gemini API
        recommendations = await callGemini(prompt);
      } else {
        // Use local matching algorithm
        recommendations = await getLocalRecommendations(userMessage, restaurants);
      }
      
      return recommendations;
    } catch (error) {
      console.error('Error generating recommendations:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getLocalRecommendations = async (userMessage, restaurants) => {
    // Simple keyword matching algorithm for demo
    const userInput = userMessage.toLowerCase();
    const keywords = userInput.split(' ');
    
    // Score restaurants based on keyword matches
    const scoredRestaurants = restaurants.map(restaurant => {
      let score = 0;
      const restaurantText = [
        restaurant.name,
        restaurant.description,
        ...(restaurant.tags || []),
        restaurant.menu_status
      ].join(' ').toLowerCase();

      keywords.forEach(keyword => {
        if (restaurantText.includes(keyword)) {
          score += 2;
        }
        if (restaurant.name.toLowerCase().includes(keyword)) {
          score += 3;
        }
        if (restaurant.tags && restaurant.tags.some(tag => tag.toLowerCase().includes(keyword))) {
          score += 4;
        }
      });

      return { ...restaurant, score };
    });

    // Sort by score and get top 5
    const topRestaurants = scoredRestaurants
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    if (topRestaurants.length === 0) {
      return "I couldn't find any restaurants matching your request. Try asking for a specific cuisine type like 'pizza', 'burgers', 'halal', or 'fine dining'.";
    }

    let response = "Here are some great options for you:\n\n";
    
    topRestaurants.forEach((restaurant, index) => {
      response += `${index + 1}. **${restaurant.name}**\n`;
      if (restaurant.description) {
        response += `   ${restaurant.description}\n`;
      }
      if (restaurant.tags && restaurant.tags.length > 0) {
        response += `   Cuisine: ${restaurant.tags.join(', ')}\n`;
      }
      if (restaurant.rating) {
        response += `   Rating: ${restaurant.rating}/5 ⭐\n`;
      }
      if (restaurant.priceLevel) {
        response += `   Price: ${'$'.repeat(restaurant.priceLevel)}\n`;
      }
      response += `   Location: ${restaurant.address}\n\n`;
    });

    return response;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message
    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);

    try {
      const recommendations = await generateRecommendations(userMessage);
      
      // Add bot response
      const botMsg = {
        id: Date.now() + 1,
        type: 'bot',
        content: recommendations,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMsg]);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessage = (content) => {
    // Simple formatting for restaurant names and features
    return content.split('\n').map((line, index) => {
      if (line.includes('**')) {
        // Bold restaurant names
        return <Typography key={index} variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>{line.replace(/\*\*/g, '')}</Typography>;
      } else if (line.trim().startsWith('•')) {
        // Bullet points
        return <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.5 }}>{line}</Typography>;
      } else if (line.trim()) {
        return <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>{line}</Typography>;
      }
      return <br key={index} />;
    });
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={() => setIsOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000
        }}
      >
        <ChatIcon />
      </Fab>

      {/* Chat Dialog */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: 'primary.main',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RestaurantIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Restaurant Assistant</Typography>
          </Box>
          <IconButton
            onClick={() => setIsOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {/* AI Toggle */}
          <Box sx={{ p: 2, backgroundColor: '#f0f0f0', borderBottom: 1, borderColor: 'divider' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useAI}
                  onChange={(e) => setUseAI(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AIIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="body2">
                    {useAI ? 'AI-Powered Recommendations' : 'Local Matching'}
                  </Typography>
                </Box>
              }
            />
            {useAI && (
              <Alert severity="info" sx={{ mt: 1 }}>
                AI mode uses Gemini for smarter recommendations. Requires API key configuration.
              </Alert>
            )}
          </Box>

          {/* Messages Area */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            p: 2,
            backgroundColor: '#f5f5f5'
          }}>
            <List sx={{ p: 0 }}>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    flexDirection: 'column',
                    alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
                    p: 0,
                    mb: 2
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '80%',
                      backgroundColor: message.type === 'user' ? 'primary.main' : 'white',
                      color: message.type === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                      boxShadow: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 1,
                          backgroundColor: message.type === 'user' ? 'white' : 'primary.main',
                          color: message.type === 'user' ? 'primary.main' : 'white'
                        }}
                      >
                        {message.type === 'user' ? 'U' : <RestaurantIcon fontSize="small" />}
                      </Avatar>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {message.type === 'user' ? 'You' : 'Assistant'}
                      </Typography>
                    </Box>
                    <Box>
                      {formatMessage(message.content)}
                    </Box>
                  </Paper>
                </ListItem>
              ))}
              
              {isLoading && (
                <ListItem sx={{ justifyContent: 'flex-start', p: 0, mb: 2 }}>
                  <Paper sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {useAI ? 'AI is thinking...' : 'Finding recommendations...'}
                      </Typography>
                    </Box>
                  </Paper>
                </ListItem>
              )}
              
              <div ref={messagesEndRef} />
            </List>
          </Box>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
              {error}
            </Alert>
          )}

          {/* Input Area */}
          <Box sx={{ p: 2, backgroundColor: 'white', borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me about restaurants..."
                variant="outlined"
                size="small"
                disabled={isLoading}
                sx={{ mr: 1 }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                color="primary"
                sx={{ mb: 0.5 }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatBotWithAI; 